'use client';

import { useEffect, useState, useRef } from 'react';
import { useAriaAnalytics } from '@/components/Aria/hooks/useAriaAnalytics';

export function AnalyticsInitializer() {
  const [analyticsStatus, setAnalyticsStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const initAttempted = useRef(false);
  const trackingAttempted = useRef(false);
  
  const { trackEvent } = useAriaAnalytics({
    endpoint: '/api/analytics',
    debug: process.env.NODE_ENV === 'development'
  });

  useEffect(() => {
    // Generate a consistent session ID if one doesn't exist
    if (typeof window !== 'undefined' && !localStorage.getItem('sessionId')) {
      const sessionId = `session_${Math.random().toString(36).substring(2, 15)}_${Date.now()}`;
      localStorage.setItem('sessionId', sessionId);
    }

    // Check if this is a form submission page - if so, don't initialize analytics
    const isFormPage = window.location.pathname.includes('/contact') || 
                       window.location.pathname.includes('/investor') ||
                       window.location.pathname.includes('/partner');
    
    // If we're on a form page, don't initialize analytics to avoid interference
    if (isFormPage) {
      console.log('Form page detected, skipping analytics initialization to avoid interference');
      return;
    }

    const initAnalytics = async () => {
      // Prevent multiple initialization attempts
      if (initAttempted.current || analyticsStatus !== 'idle') return;
      
      initAttempted.current = true;
      setAnalyticsStatus('loading');
      
      try {
        // Create an AbortController to handle timeouts - reduced from 20s to 5s
        const controller = new AbortController();
        const timeoutId = setTimeout(() => {
          controller.abort();
          // Instead of throwing an error, handle it gracefully
          console.warn('Analytics initialization timed out after 5 seconds');
          setErrorDetails('Initialization timed out');
          setAnalyticsStatus('error');
        }, 5000); // Reduced timeout to 5 seconds
        
        const sessionId = localStorage.getItem('sessionId') || 'anonymous';
        
        // Use try-catch for the fetch to prevent unhandled promise rejections
        try {
          // Prepare the request body first to ensure it's valid JSON
          const requestBody = {
            event: 'init',
            metadata: {
              platform: 'web',
              timestamp: new Date().toISOString(),
              user_agent: navigator.userAgent,
              source: 'analytics_initializer',
              category: 'system',
              action: 'initialize'
            }
          };
          
          // Validate that we have a proper JSON object before sending
          const bodyString = JSON.stringify(requestBody);
          if (!bodyString || bodyString === '{}' || bodyString === 'null') {
            throw new Error('Invalid request body generated');
          }

          const response = await fetch('/api/analytics?endpoint=init', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-session-id': sessionId
            },
            credentials: 'include',
            signal: controller.signal,
            body: bodyString,
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            const data = await response.json().catch(() => ({ message: response.statusText }));
            const errorMessage = data.message || `Server returned ${response.status}: ${response.statusText}`;
            console.error('Failed to initialize analytics:', errorMessage);
            setErrorDetails(errorMessage);
            setAnalyticsStatus('error');
          } else {
            const data = await response.json();
            console.log('Analytics initialized successfully:', data);
            setAnalyticsStatus('success');
            
            // Only track if we haven't already attempted to track
            if (!trackingAttempted.current) {
              trackingAttempted.current = true;
              // Use a shorter timeout
              setTimeout(() => {
                try {
                  trackEvent({
                    category: 'System',
                    action: 'AnalyticsInitialized',
                    label: 'Success',
                    metadata: {
                      source: 'analytics_initializer',
                      category: 'System',
                      action: 'AnalyticsInitialized'
                    }
                  });
                } catch (trackError) {
                  console.error('Failed to track analytics initialization:', trackError);
                }
              }, 1000); // Reduced delay to 1 second
            }
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          console.error('Analytics fetch error:', fetchError);
          setErrorDetails(fetchError instanceof Error ? fetchError.message : 'Fetch error');
          setAnalyticsStatus('error');
        }
      } catch (error) {
        let errorMessage = 'Unknown error';
        
        if (error instanceof Error) {
          errorMessage = error.message;
          console.error('Analytics initialization error:', error.message);
          if (error.stack) {
            console.debug('Error stack:', error.stack);
          }
        } else {
          console.error('Unknown analytics initialization error:', error);
        }
        
        setErrorDetails(errorMessage);
        setAnalyticsStatus('error');
      }
    };

    // Initialize analytics with a delay to ensure it doesn't interfere with page load
    const timeoutId = setTimeout(() => {
      initAnalytics();
    }, 2000); // Delay analytics initialization by 2 seconds
    
    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
    };
  }, [trackEvent, analyticsStatus]);

  // This component doesn't render anything visible
  return null;
}
